import { useMutation } from "@apollo/client";
import { ChevronDown, Loader2 } from "lucide-react";
import { SET_LEAD_TRIAGE } from "../../apollo/leads";
import {
  TRIAGE_STATUSES,
  TRIAGE_LABEL,
  TRIAGE_BADGE_CLASS,
  isTriageStatus,
  type TriageStatus,
} from "../../data/leadTriage";
import { useToastPush } from "./Toast";

interface TriageControlProps {
  leadId: string;
  /** Current triage_status from the server (falls back to "new" if unknown). */
  value: string;
  size?: "sm" | "md";
  className?: string;
}

/**
 * Inline setter for a lead's triage_status (#476) — the human working state,
 * a separate axis from the funnel's `status`. A native <select> styled as the
 * triage badge, so it reads the current state and sets a new one in one click.
 * Stops click propagation so it can live inside a clickable table row.
 */
export function TriageControl({
  leadId,
  value,
  size = "sm",
  className = "",
}: TriageControlProps) {
  const toast = useToastPush();
  const [setTriage, { loading }] = useMutation(SET_LEAD_TRIAGE);
  const current: TriageStatus = isTriageStatus(value) ? value : "new";

  const handleChange = async (next: string) => {
    if (next === current || !isTriageStatus(next)) return;
    try {
      await setTriage({
        variables: { ids: [leadId], triageStatus: next },
        optimisticResponse: {
          setLeadTriage: [
            { __typename: "Lead", id: leadId, triageStatus: next },
          ],
        },
      });
    } catch {
      toast({ kind: "error", title: "Couldn't update triage state" });
    }
  };

  const pad =
    size === "md" ? "pl-2.5 pr-6 py-1 text-[12.5px]" : "pl-2 pr-6 py-0.5 text-[11px]";

  return (
    <div
      className={`relative inline-flex items-center ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      <select
        value={current}
        disabled={loading}
        onChange={(e) => handleChange(e.target.value)}
        aria-label="Set triage state"
        className={`appearance-none rounded-full font-medium cursor-pointer whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-tide/40 disabled:opacity-60 ${pad} ${TRIAGE_BADGE_CLASS[current]}`}
      >
        {TRIAGE_STATUSES.map((s) => (
          <option key={s} value={s} className="bg-paper-hi text-ink">
            {TRIAGE_LABEL[s]}
          </option>
        ))}
      </select>
      {loading ? (
        <Loader2
          size={11}
          strokeWidth={2}
          className="animate-spin absolute right-1.5 pointer-events-none opacity-70"
        />
      ) : (
        <ChevronDown
          size={11}
          strokeWidth={2}
          className="absolute right-1.5 pointer-events-none opacity-70"
        />
      )}
    </div>
  );
}
